import { motion } from "framer-motion";
import { AlertTriangle, Shield, TrendingUp, Clock, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const benefits = [
  {
    icon: Shield,
    title: "Evita multas y recargos",
    description: "Regulariza tu situación antes de que el SAT te notifique"
  },
  {
    icon: TrendingUp,
    title: "Recupera tu historial crediticio",
    description: "Un RFC limpio mejora tu perfil ante bancos e instituciones"
  },
  {
    icon: Clock,
    title: "Proceso rápido y sin complicaciones",
    description: "Nosotros nos encargamos de todo, tú solo firma"
  }
];

const situations = [
  "No has declarado en meses o años",
  "Tienes cartas invitación del SAT",
  "Quieres cambiar de régimen fiscal",
  "Necesitas ponerte al corriente con tus obligaciones",
  "Tienes adeudos fiscales pendientes"
];

export const RegularizacionSection = () => {
  const whatsappMessage = encodeURIComponent(
    "Hola, me interesa regularizar mi situación fiscal. ¿Pueden ayudarme?"
  );

  return (
    <section className="py-20 bg-gradient-to-br from-destructive/5 via-background to-secondary/5">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full mb-6">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-medium">¿Tienes atrasos con el SAT?</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Regularización Fiscal
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            No dejes que los problemas fiscales crezcan. Te ayudamos a ponerte al corriente 
            con el SAT de forma rápida, segura y sin estrés.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Situations */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-card border border-border rounded-2xl p-8 shadow-lg"
          >
            <h3 className="text-xl font-semibold text-foreground mb-6">
              ¿Te identificas con alguna de estas situaciones?
            </h3>
            <ul className="space-y-4">
              {situations.map((situation, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{situation}</span>
                </motion.li>
              ))}
            </ul>
            
            <div className="mt-8 p-4 bg-secondary/10 rounded-xl border border-secondary/20">
              <p className="text-sm text-foreground font-medium">
                💡 Entre más pronto actúes, menores serán los recargos y multas
              </p>
            </div>
          </motion.div>

          {/* Right: Benefits and CTA */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-6"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                className="flex items-start gap-4 p-4 bg-card/50 rounded-xl border border-border/50 hover:border-secondary/30 transition-colors"
              >
                <div className="p-3 bg-secondary/10 rounded-lg">
                  <benefit.icon className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">{benefit.title}</h4>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.7 }}
              className="pt-4"
            >
              <Button
                size="lg"
                className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold"
                asChild
              >
                <a
                  href={`https://wa.me/525586879082?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2"
                >
                  Quiero regularizarme
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <p className="text-center text-xs text-muted-foreground mt-3">
                Te contactamos en menos de 24 horas para evaluar tu caso
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
